import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";
import {
  Drawer,
  AppBar,
  Toolbar,
  List,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Switch,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import SettingsIcon from "@mui/icons-material/Settings";


import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";

const drawerWidth = 240;
const DESKTOP_DRAWER_STORAGE_KEY = "drawerOpenDesktop";
const LEGACY_DRAWER_STORAGE_KEY = "drawerOpen";
const getAutoTabSmartStorageKey = (userId) => `tickets:autoTabSmart:${userId}`;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    width: "100%",
    height: "100dvh",
    overflow: "hidden",
  },
  toolbar: {
    minHeight: 56,
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    [theme.breakpoints.up("md")]: {
      minHeight: 64,
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(2),
    },
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    minHeight: 56,
    [theme.breakpoints.up("md")]: {
      minHeight: 64,
    },
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.mode === 'dark' ? "#111b21" : "#ffffff",
    boxShadow: "none",
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? "#2a3942" : "#ebeae2"}`,
  },
  drawer: {
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    overflowX: "hidden",
    width: theme.spacing(7),
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  appBarShiftCollapsed: {
    marginLeft: theme.spacing(7),
    width: `calc(100% - ${theme.spacing(7)})`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(9),
      width: `calc(100% - ${theme.spacing(9)})`,
    },
  },
  menuButton: {
    marginRight: theme.spacing(1.5),
    color: "#667781",
    [theme.breakpoints.up("md")]: {
      marginRight: theme.spacing(3),
    },
  },
  menuButtonHidden: {
    display: "none",
  },
  drawerToggleButton: {
    color: "#667781",
  },
  brandContainer: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    minWidth: 0,
  },
  brandLogo: {
    height: 32,
    width: "auto",
    maxWidth: "100%",
    objectFit: "contain",
    display: "block",
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.mode === 'dark' ? "#111b21" : "#ffffff",
    borderRight: `1px solid ${theme.palette.mode === 'dark' ? "#2a3942" : "#ebeae2"}`,
  },
  drawerPaperMobile: {
    width: 280,
    maxWidth: "80vw",
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
    "& .MuiListItemText-root": {
      display: "none",
    },
    "& .MuiListSubheader-root": {
      display: "none",
    },
    "& .MuiListItemIcon-root": {
      minWidth: 0,
      marginRight: 0,
      justifyContent: "center",
    },
    "& .MuiListItemButton-root": {
      justifyContent: "center",
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  appBarSpacer: {
    minHeight: 56,
    [theme.breakpoints.up("md")]: {
      minHeight: 64,
    },
  },
  migrationBanner: {
    flexShrink: 0,
    padding: theme.spacing(1.25, 2),
    backgroundColor: theme.palette.mode === "dark" ? "#173c36" : "#e4f5ee",
    color: theme.palette.mode === "dark" ? "#e4f5ee" : "#124735",
    borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#286457" : "#b8dfce"}`,
    fontSize: "0.875rem",
    lineHeight: 1.5,
    overflowWrap: "anywhere",
  },
  migrationLink: {
    color: "inherit",
    fontWeight: 700,
    textDecoration: "underline",
  },
  content: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  switch: {
    transform: "scale(0.85)",
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
  },
  themeIcon: {
    color: theme.palette.text.primary,
  }
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [desktopDrawerOpen, setDesktopDrawerOpen] = useState(() => {
    const savedDesktopPreference = localStorage.getItem(DESKTOP_DRAWER_STORAGE_KEY);
    if (savedDesktopPreference !== null) {
      return savedDesktopPreference === "true";
    }

    const legacyDrawerPreference = localStorage.getItem(LEGACY_DRAWER_STORAGE_KEY);
    if (legacyDrawerPreference !== null) {
      const normalizedLegacyPreference = legacyDrawerPreference === "true";
      localStorage.setItem(DESKTOP_DRAWER_STORAGE_KEY, String(normalizedLegacyPreference));
      localStorage.removeItem(LEGACY_DRAWER_STORAGE_KEY);
      return normalizedLegacyPreference;
    }

    return true;
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const [autoTabSmartEnabled, setAutoTabSmartEnabled] = useState(true);

  const drawerVariant = isDesktop ? "permanent" : "temporary";
  const drawerOpen = isDesktop ? desktopDrawerOpen : mobileDrawerOpen;

  const handleDrawerClose = () => {
    if (isDesktop) {
      setDesktopDrawerOpen(false);
      localStorage.setItem(DESKTOP_DRAWER_STORAGE_KEY, "false");
      return;
    }

    setMobileDrawerOpen(false);
  };

  const handleDrawerToggle = () => {
    if (isDesktop) {
      setDesktopDrawerOpen((prev) => {
        const next = !prev;
        localStorage.setItem(DESKTOP_DRAWER_STORAGE_KEY, next);
        return next;
      });
      return;
    }

    setMobileDrawerOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isDesktop) {
      setMobileDrawerOpen(false);
      return;
    }

    setMobileDrawerOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    const migrateLegacyDrawerState = () => {
      const legacyDrawerPreference = localStorage.getItem(LEGACY_DRAWER_STORAGE_KEY);
      if (legacyDrawerPreference === null) return;

      if (localStorage.getItem(DESKTOP_DRAWER_STORAGE_KEY) === null) {
        localStorage.setItem(
          DESKTOP_DRAWER_STORAGE_KEY,
          String(legacyDrawerPreference === "true")
        );
      }

      localStorage.removeItem(LEGACY_DRAWER_STORAGE_KEY);
    };

    migrateLegacyDrawerState();
  }, []);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    try {
      const savedAutoTabSmart = localStorage.getItem(getAutoTabSmartStorageKey(user.id));
      if (savedAutoTabSmart === null) {
        setAutoTabSmartEnabled(true);
      } else {
        setAutoTabSmartEnabled(savedAutoTabSmart === "true");
      }
    } catch (_err) {
      setAutoTabSmartEnabled(true);
    }
  }, [user?.id]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleSettingsMenuOpen = (event) => {
    setSettingsAnchorEl(event.currentTarget);
    setSettingsMenuOpen(true);
  };

  const handleSettingsMenuClose = () => {
    setSettingsAnchorEl(null);
    setSettingsMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleToggleAutoSmart = () => {
    if (!user?.id) {
      return;
    }

    setAutoTabSmartEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(getAutoTabSmartStorageKey(user.id), String(next));
      window.dispatchEvent(
        new CustomEvent("tickets-auto-smart-changed", {
          detail: { userId: user.id, enabled: next },
        })
      );
      return next;
    });
  };

  const drawerClose = () => {
    if (!isDesktop) {
      setMobileDrawerOpen(false);
    }
  };

  const greenActiveSwitchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#00a884",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#00a884",
      opacity: 1,
    },
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={clsx(
          classes.drawer,
          isDesktop && (desktopDrawerOpen ? classes.drawerOpen : classes.drawerClose)
        )}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !isDesktop && classes.drawerPaperMobile,
            isDesktop && !desktopDrawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={handleDrawerToggle} className={classes.drawerToggleButton}>
            {isDesktop && !desktopDrawerOpen ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </div>
        <Divider />
        <List sx={{ mt: 2 }}>
          <MainListItems
            drawerClose={drawerClose}
            collapsed={isDesktop && !desktopDrawerOpen}
          />
        </List>
        <Divider />
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(
          classes.appBar,
          isDesktop &&
            (desktopDrawerOpen ? classes.appBarShift : classes.appBarShiftCollapsed)
        )}
        color="inherit"
        style={{
          backgroundColor: darkMode ? "#111b21" : "#ffffff",
          boxShadow: "none",
          borderBottom: `1px solid ${darkMode ? "#2a3942" : "#ebeae2"}`
        }}
      >
        <Toolbar className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            className={clsx(
              classes.menuButton,
              isDesktop && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>

          <div className={classes.brandContainer}>
            <img
              src="/logo_header.png"
              alt="ChatMessage"
              className={classes.brandLogo}
            />
          </div>

          <IconButton
            aria-label="settings"
            aria-controls="menu-settings"
            aria-haspopup="true"
            onClick={handleSettingsMenuOpen}
            className={classes.iconButton}
          >
            <SettingsIcon />
          </IconButton>

          <Menu
            id="menu-settings"
            anchorEl={settingsAnchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={settingsMenuOpen}
            onClose={handleSettingsMenuClose}
          >
            <MenuItem onClick={(e) => e.stopPropagation()}>
              <Brightness4Icon className={classes.themeIcon} />
              <ListItemText sx={{ ml: 1 }} primary="Modo oscuro" />
              <Switch
                checked={darkMode}
                onChange={toggleTheme}
                color="default"
                className={classes.switch}
                sx={greenActiveSwitchSx}
                inputProps={{ "aria-label": "toggle dark mode" }}
              />
            </MenuItem>
            <MenuItem onClick={(e) => e.stopPropagation()}>
              <SettingsIcon className={classes.themeIcon} />
              <ListItemText sx={{ ml: 1 }} primary="Modo Smart (Tickets)" />
              <Switch
                checked={autoTabSmartEnabled}
                onChange={handleToggleAutoSmart}
                color="default"
                className={classes.switch}
                sx={greenActiveSwitchSx}
                inputProps={{ "aria-label": "toggle smart tickets mode" }}
              />
            </MenuItem>
          </Menu>

          {user?.id && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content} style={{ position: "relative" }}>
        <div className={classes.appBarSpacer} />
        <div className={classes.migrationBanner} role="status">
          <strong>Nos hemos mudado a la nueva app de WhatsApp.</strong>{" "}
          Entra en{" "}
          <a
            className={classes.migrationLink}
            href="https://wacrm.arvera.es/"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://wacrm.arvera.es/
          </a>
          . Usa el mismo usuario y la contraseña: <strong>rme39msu</strong>.
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {children ? children : null}
        </div>
      </main>
    </div>
  );
};

export default LoggedInLayout;
